import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BellOff, UserPlus } from "lucide-react";
import { api } from "@/lib/api";
import { ukDay } from "@/lib/time";
import { Card, Note } from "@/components/hub/Shell";

/** Partners who signed themselves up from /partner-with-us and are waiting on the desk. */
export function PendingPartnersCard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/hub/partners/pending").then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  }, []);
  const items = data?.items || [];
  if (!data || items.length === 0) return null;
  return (
    <Card label="Waiting for your approval" Icon={UserPlus} tone="act" testId="hub-pending-partners"
          right={<Link to="/hub/referrers?pending=1" className="text-[12px] font-semibold text-[#0055FF] hover:underline" data-testid="hub-pending-partners-all">Review all</Link>}>
      <div className="mt-3 divide-y divide-slate-100">
        {items.slice(0, 5).map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`hub-pending-partner-${p.id}`}>
            <div className="min-w-0">
              <div className="truncate text-[13px] font-medium text-slate-900">{p.company || p.name}</div>
              <div className="truncate text-[11.5px] text-slate-500">
                {p.signup?.kind === "media" ? "Media & advertising" : "Call centre"}
                {p.signup?.scale ? ` · ${p.signup.scale}` : ""}
                {p.referred_by_name ? ` · via ${p.referred_by_name}` : ""}
                {p.created_at ? ` · ${ukDay(p.created_at)}` : ""}
              </div>
            </div>
            <Link to="/hub/referrers?pending=1" className="btn btn-primary btn-sm shrink-0" data-testid={`hub-pending-partner-review-${p.id}`}>Review</Link>
          </div>
        ))}
      </div>
      <Note>They can sign in already. Cases and links stay locked until you approve — and the agreement goes out the moment you do.</Note>
    </Card>
  );
}

/** Call-centre seats that have gone quiet — no case and no sign-in for a fortnight. */
export function QuietSeatsCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/hub/seats/quiet").then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  useEffect(() => { load(); }, []);
  const items = data?.items || [];
  if (!data || items.length === 0) return null;

  const nudge = async (row) => {
    setBusy(row.seat_id);
    try {
      const r = await api.post(`/hub/seats/${row.seat_id}/nudge`);
      toast.success(r.data.emailed ? `${row.holder_name || row.company} has been nudged about ${row.seat_name}` : "Logged — the email didn't go out");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send the nudge"); }
    finally { setBusy(null); }
  };

  return (
    <Card label={`Seats gone quiet · ${data.days} days`} Icon={BellOff} tone="act" testId="hub-quiet-seats">
      <div className="mt-3 divide-y divide-slate-100">
        {items.slice(0, 6).map((r) => (
          <div key={r.seat_id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`hub-quiet-seat-${r.seat_id}`}>
            <div className="min-w-0">
              <div className="truncate text-[13px] font-medium text-slate-900">
                {r.seat_name} <span className="font-normal text-slate-500">at {r.company}</span>
              </div>
              <div className="truncate text-[11.5px] text-slate-500">
                <span className="font-mono tabular-nums text-[#3B63A8]">{r.days_quiet}d</span> quiet
                {" · "}{r.cases} case{r.cases === 1 ? "" : "s"} ever
                {r.last_case_at ? ` · last ${ukDay(r.last_case_at)}` : " · never put one in"}
                {r.nudged_at ? ` · nudged ${r.nudged_days_ago === 0 ? "today" : `${r.nudged_days_ago}d ago`}` : ""}
              </div>
            </div>
            <button className="btn btn-ghost btn-sm shrink-0" disabled={busy === r.seat_id || (r.nudged_days_ago != null && r.nudged_days_ago < 7)}
                    onClick={() => nudge(r)} data-testid={`hub-quiet-seat-nudge-${r.seat_id}`}
                    title={r.nudged_days_ago != null && r.nudged_days_ago < 7 ? "Nudged in the last week" : `Email ${r.holder_name || "the account holder"}`}>
              {busy === r.seat_id ? "Sending…" : r.nudged_days_ago != null && r.nudged_days_ago < 7 ? "Nudged" : "Nudge the holder"}
            </button>
          </div>
        ))}
      </div>
      <Note>One email to the account holder, not the agent: is everything all right, and here's how to get them back in or remove the seat.</Note>
    </Card>
  );
}
